import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";

export type ModuleRequest = {
  id: string;
  module_id: string;
  status: string;
};

interface Props {
  modules: Record<string, boolean>;
  moduleRequests: ModuleRequest[];
  onRequestSent?: (moduleId: string) => void;
}

type ModuleStatus = "active" | "pending" | "locked";

const MODULE_CATALOG: {
  id: string;
  label: string;
  icon: string;
  desc: string;
  href: string;
  category: string;
}[] = [
  {
    id: "digital_invitations",
    label: "Digital Invitations",
    icon: "ri-mail-send-line",
    desc: "Branded event invitations with RSVP",
    href: "/admin/invitations",
    category: "Client Experience",
  },
  {
    id: "signature_books",
    label: "Signature Book",
    icon: "ri-book-open-line",
    desc: "Digital guestbook for events",
    href: "/admin/signature-books",
    category: "Client Experience",
  },
  {
    id: "photo_selection",
    label: "Photo Selection",
    icon: "ri-image-2-line",
    desc: "Clients select favorite photos",
    href: "/admin/jobs",
    category: "Production",
  },
  {
    id: "final_deliveries",
    label: "Final Deliveries",
    icon: "ri-send-plane-line",
    desc: "Deliver galleries to clients",
    href: "/admin/jobs",
    category: "Production",
  },
  {
    id: "media_packages",
    label: "DVD / Media",
    icon: "ri-disc-line",
    desc: "USB, albums, print packages",
    href: "/admin/media-packages",
    category: "Production",
  },
  {
    id: "ai_sales_assistant",
    label: "AI Sales Assistant",
    icon: "ri-robot-line",
    desc: "AI-powered lead follow-up & Q&A",
    href: "/admin/settings/ai-assistant",
    category: "Marketing",
  },
  {
    id: "appointments",
    label: "Appointments",
    icon: "ri-calendar-check-line",
    desc: "Client booking & scheduling",
    href: "/admin/appointments",
    category: "Marketing",
  },
  {
    id: "whatsapp_notifications",
    label: "WhatsApp",
    icon: "ri-whatsapp-line",
    desc: "Automated WhatsApp notifications",
    href: "/admin/settings/integrations",
    category: "Notifications",
  },
  {
    id: "custom_domain",
    label: "Custom Domain",
    icon: "ri-global-line",
    desc: "Use your own branded domain",
    href: "/admin/website",
    category: "Branding",
  },
  {
    id: "automations",
    label: "Automations",
    icon: "ri-flow-chart",
    desc: "Automated workflows & triggers",
    href: "/admin/automations",
    category: "Advanced",
  },
  {
    id: "reminders",
    label: "Reminders",
    icon: "ri-notification-3-line",
    desc: "Automated client reminders",
    href: "/admin/reminders",
    category: "Advanced",
  },
];

const STATUS_CONFIG: Record<ModuleStatus, { label: string; dotClass: string; badgeClass: string }> = {
  active:  { label: "Active",   dotClass: "bg-emerald-400", badgeClass: "bg-emerald-100 text-emerald-700" },
  pending: { label: "Pending",  dotClass: "bg-amber-400",   badgeClass: "bg-amber-100 text-amber-700"     },
  locked:  { label: "Locked",   dotClass: "bg-slate-300",   badgeClass: "bg-slate-100 text-slate-400"     },
};

export default function DashboardModules({ modules, moduleRequests, onRequestSent }: Props) {
  const [requesting, setRequesting] = useState<string | null>(null);
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());

  const pendingIds = new Set(
    moduleRequests
      .filter((r) => r.status === "pending" || r.status === "requested")
      .map((r) => r.module_id)
  );

  const getStatus = (moduleId: string): ModuleStatus => {
    if (modules[moduleId]) return "active";
    if (pendingIds.has(moduleId) || sentRequests.has(moduleId)) return "pending";
    return "locked";
  };

  const activeCount  = MODULE_CATALOG.filter((m) => getStatus(m.id) === "active").length;
  const pendingCount = MODULE_CATALOG.filter((m) => getStatus(m.id) === "pending").length;
  const lockedCount  = MODULE_CATALOG.filter((m) => getStatus(m.id) === "locked").length;

  const handleRequest = useCallback(async (moduleId: string) => {
    if (requesting) return;
    setRequesting(moduleId);
    try {
      const tenantId = await getTenantId();
      if (!tenantId) return;

      // Upsert: avoid duplicate requests
      await supabase.from("tenant_module_requests").insert({
        tenant_id: tenantId,
        module_id: moduleId,
        status: "pending",
        message: "Requested from dashboard module status",
      });

      setSentRequests((prev) => new Set([...prev, moduleId]));
      onRequestSent?.(moduleId);
    } finally {
      setRequesting(null);
    }
  }, [requesting, onRequestSent]);

  // Group by category
  const categories = Array.from(new Set(MODULE_CATALOG.map((m) => m.category)));

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
            <i className="ri-puzzle-line text-sm text-[#a6853e]"></i>
          </span>
          <div>
            <h2 className="text-[#0f172a] font-semibold text-sm">Module Status</h2>
            <p className="text-slate-400 text-xs">Features available in your plan</p>
          </div>
        </div>

        {/* Summary pills */}
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
            {activeCount} active
          </span>
          {pendingCount > 0 && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
              {pendingCount} pending
            </span>
          )}
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 inline-block"></span>
            {lockedCount} locked
          </span>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {categories.map((cat) => {
          const catModules = MODULE_CATALOG.filter((m) => m.category === cat);
          return (
            <div key={cat}>
              <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest mb-2.5">{cat}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {catModules.map((mod) => {
                  const status = getStatus(mod.id);
                  const cfg = STATUS_CONFIG[status];
                  const isRequesting = requesting === mod.id;

                  return (
                    <div
                      key={mod.id}
                      className={`flex items-start gap-3 p-3.5 rounded-xl border transition-all ${
                        status === "active"
                          ? "bg-slate-50 border-slate-200 hover:border-[#C8A96E]/30"
                          : status === "pending"
                          ? "bg-amber-50/50 border-amber-100"
                          : "bg-slate-50/50 border-slate-200 opacity-70 hover:opacity-90"
                      }`}
                    >
                      {/* Icon */}
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${
                          status === "active"
                            ? "bg-[#C8A96E]/10 text-[#a6853e]"
                            : status === "pending"
                            ? "bg-amber-100 text-amber-600"
                            : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        <i className={`${mod.icon} text-sm`}></i>
                      </span>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p
                            className={`text-xs font-semibold leading-tight ${
                              status === "active" ? "text-[#0f172a]" : "text-slate-400"
                            }`}
                          >
                            {mod.label}
                          </p>
                          {/* Status badge */}
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1 ${cfg.badgeClass}`}>
                            <span className={`w-1 h-1 rounded-full ${cfg.dotClass} inline-block`}></span>
                            {cfg.label}
                          </span>
                        </div>

                        <p className="text-slate-400 text-[11px] mt-0.5 leading-tight">{mod.desc}</p>

                        {/* Action row */}
                        <div className="mt-2">
                          {status === "active" && (
                            <Link
                              to={mod.href}
                              className="inline-flex items-center gap-1 text-[11px] text-[#a6853e] hover:text-[#C8A96E] font-medium transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-arrow-right-line text-[10px]"></i>
                              Open
                            </Link>
                          )}
                          {status === "pending" && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 font-medium whitespace-nowrap">
                              <i className="ri-time-line text-[10px]"></i>
                              Awaiting approval
                            </span>
                          )}
                          {status === "locked" && (
                            <button
                              onClick={() => handleRequest(mod.id)}
                              disabled={isRequesting}
                              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#0f172a] font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              {isRequesting ? (
                                <>
                                  <i className="ri-loader-4-line text-[10px] animate-spin"></i>
                                  Requesting…
                                </>
                              ) : (
                                <>
                                  <i className="ri-add-circle-line text-[10px]"></i>
                                  Request Activation
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}